import React from 'react';
import { Modal, Box, Typography, Stack, Divider, IconButton, Chip, CircularProgress } from '@mui/material';
import Iconify from '../../../components/Iconify';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 600 },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  maxHeight: '90vh',
  overflow: 'auto',
};

const EmployeeProfileModal = ({ open, onClose, employee, loading }) => {
  if (loading) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box sx={style}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        </Box>
      </Modal>
    );
  }

  if (!employee) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box sx={style}>
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" textAlign="center">
              No se encontró información del empleado
            </Typography>
          </Box>
        </Box>
      </Modal>
    );
  }

  const fullName = `${employee.first_name} ${employee.second_name || ''} ${employee.first_last_name} ${
    employee.second_last_name || ''
  }`.trim();

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="employee-profile-modal">
      <Box sx={style}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'primary.contrastText',
            }}
          >
            <Iconify icon="material-symbols:close" />
          </IconButton>
          <Typography variant="h5" component="h2" gutterBottom>
            Perfil del Empleado
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Información detallada
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Información Personal */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                Información Personal
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Nombre Completo
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {fullName}
                  </Typography>
                </Box>

                {employee.employee_number && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Número de Empleado
                    </Typography>
                    <Typography variant="body1">{employee.employee_number}</Typography>
                  </Box>
                )}

                {employee.address && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Dirección
                    </Typography>
                    <Typography variant="body1">{employee.address}</Typography>
                  </Box>
                )}

                {employee.chain && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Cadena
                    </Typography>
                    <Typography variant="body1">{employee.chain}</Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Información de Cuenta */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                Información de Cuenta
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Correo Electrónico
                  </Typography>
                  <Typography variant="body1">{employee.user?.email || 'No disponible'}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Estado
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={employee.is_active ? 'Activo' : 'Inactivo'}
                      color={employee.is_active ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* Tiendas Asignadas */}
            {employee.stores && employee.stores.length > 0 && (
              <>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                    Tiendas Asignadas
                  </Typography>
                  <Stack spacing={1}>
                    {employee.stores.map((store) => (
                      <Box
                        key={store.uuid}
                        sx={{
                          p: 2,
                          bgcolor: 'grey.100',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {store.name}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
                <Divider />
              </>
            )}

            {/* Fechas */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                Información Adicional
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Fecha de Creación
                  </Typography>
                  <Typography variant="body2">
                    {new Date(employee.created_at).toLocaleDateString('es-MX', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Última Actualización
                  </Typography>
                  <Typography variant="body2">
                    {new Date(employee.updated_at).toLocaleDateString('es-MX', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default EmployeeProfileModal;
